"use client";

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
} from "react";
import { useRouter, usePathname } from "next/navigation";
import { useSession, signOut } from "next-auth/react";
import { jwtDecode } from "jwt-decode";
import { isPublicRoute } from "../utils/publicPaths";

export const CreateContext = createContext();
export const useAppContext = () => useContext(CreateContext);

const Context = ({ children }) => {
  // Estados de la UI
  const [mobile, setMobile] = useState(true);
  const [rightBar, setRightBar] = useState(true);
  const [toggleTop, setToggle] = useState(true);
  const [toggleAuth, setToggleAuth] = useState(true);
  const [showItem, setShowItem] = useState(true);
  const [activeMobileMenu, setActiveMobileMenu] = useState(true);
  const [isLightTheme, setLightTheme] = useState(true);
  const [bitsRefreshTick, setBitsRefreshTick] = useState(0);
  const triggerBitsRefresh = () => setBitsRefreshTick((t) => t + 1);

  const { data: session, status } = useSession();
  const router = useRouter();
  const pathname = usePathname();

  // **1. Verificar Expiración del Token con Intervalo**
  useEffect(() => {
    let intervalId;

    if (status === "authenticated" && session?.token) {
      const decoded = jwtDecode(session.token);

      const handleTokenExpiration = () => {
        const currentTime = Date.now() / 1000;

        if (decoded.exp < currentTime) {
          // Sincronizar logout en otras pestañas
          localStorage.setItem("logout-TutorIAPlus", Date.now());

          if (!isPublicRoute(pathname)) {
            // Si la ruta es protegida, hacer signOut con redirección
            signOut({
              callbackUrl: `/iniciar-sesion?callbackUrl=${encodeURIComponent(
                pathname
              )}`,
            });
          } else {
            // En ruta pública, realizar signOut sin redirección
            signOut({ redirect: false });
          }
        }
      };

      // **1. Validación inicial del token**
      handleTokenExpiration();

      // **2. Establecer el intervalo para verificar periódicamente**
      intervalId = setInterval(handleTokenExpiration, 60000); // Cada 60 segundos
    }

    return () => {
      // Limpiar el intervalo cuando el componente se desmonte o cambien las dependencias
      clearInterval(intervalId);
    };
  }, [status, session, pathname]);

  // **2. Listener de Logout Compartido**
  useEffect(() => {
    const syncLogout = (event) => {
      if (event.key === "logout-TutorIAPlus") {
        if (!isPublicRoute(pathname)) {
          signOut({
            callbackUrl: `/iniciar-sesion?callbackUrl=${encodeURIComponent(
              pathname
            )}`,
          });
        } else {
          signOut({ redirect: false });
        }
      }
    };

    window.addEventListener("storage", syncLogout);
    return () => window.removeEventListener("storage", syncLogout);
  }, [pathname]);

  // **3. Manejo de Estado y Temas**
  const checkScreenSize = () => {
    if (window.innerWidth < 1600) {
      setMobile(false);
    } else {
      setMobile(true);
    }

    if (window.innerWidth < 1366) {
      setRightBar(false);
    } else {
      setRightBar(true);
    }
  };

  // Cargar el tema guardado en localStorage al montar el componente
  useEffect(() => {
    const themeType = localStorage.getItem("aiwave-theme");
    if (themeType === "dark") {
      setLightTheme(false);
      document.body.classList.add("active-dark-mode");
    }
  }, []);

  // Actualizar el tema en el DOM y en localStorage según isLightTheme
  useEffect(() => {
    if (isLightTheme) {
      document.body.classList.remove("active-dark-mode");
      localStorage.setItem("aiwave-theme", "light");
    } else {
      document.body.classList.add("active-dark-mode");
      localStorage.setItem("aiwave-theme", "dark");
    }
  }, [isLightTheme]);

  // Función para alternar el tema
  const toggleTheme = () => {
    setLightTheme((prevTheme) => !prevTheme);
  };

  // Ajustar la UI según el tamaño de la ventana
  useEffect(() => {
    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    return () => {
      window.removeEventListener("resize", checkScreenSize);
    };
  }, []);

  const shouldCollapseLeftbar = !mobile;
  const shouldCollapseRightbar = !rightBar;

  // **4. Manejo de Sesión: Redirigir si el usuario no está autenticado**
  useEffect(() => {
    if (status === "unauthenticated") {
      // Redirigir solo si la ruta actual no es pública
      if (!isPublicRoute(pathname)) {
        router.push(
          `/iniciar-sesion?callbackUrl=${encodeURIComponent(pathname)}`
        );
      }
    }
  }, [status, pathname, router]);

  const value = useMemo(
    () => ({
      mobile,
      setMobile,
      showItem,
      setShowItem,
      activeMobileMenu,
      setActiveMobileMenu,
      toggleTop,
      setToggle,
      toggleAuth,
      setToggleAuth,
      rightBar,
      setRightBar,
      shouldCollapseLeftbar,
      shouldCollapseRightbar,
      isLightTheme,
      setLightTheme,
      toggleTheme,
      bitsRefreshTick,
      triggerBitsRefresh,
    }),
    [
      mobile,
      showItem,
      activeMobileMenu,
      toggleTop,
      toggleAuth,
      rightBar,
      shouldCollapseLeftbar,
      shouldCollapseRightbar,
      isLightTheme,
      bitsRefreshTick,
    ]
  );

  return (
    <CreateContext.Provider value={value}>{children}</CreateContext.Provider>
  );
};

export default Context;
